import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CreateCampaign } from "../components/CreateCampaign";
import { DonateComingSoonDialog } from "../components/DonateComingSoonDialog";
import { FadeUp } from "../components/Reveal";
import { PageAccent } from "../components/PageAccent";
import { EmSwash } from "../components/EmSwash";
import { NoteEyebrow } from "../components/NoteEyebrow";
import { FounderSignature } from "../components/FounderSignature";
import { SEO } from "../lib/SEO";
import { useTeamData, buildRoster, focusStyle, memberKey } from "../lib/team";
import { TeamGlance } from "../components/team/TeamGlance";
import { LitGround } from "../components/PaperLight";

const initials = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/**
 * Placeholder tile used when a real photo hasn't been uploaded yet.
 * Renders the accent-colored monogram on a soft hatched card.
 */
const PhotoPlaceholder = ({ name, accent, variant }) => {
  const then = variant === "then";
  const hatch = then ? "hsl(49 75% 68% / 0.55)" : "hsl(var(--muted))";
  return (
    <div className={`relative flex h-full w-full items-center justify-center overflow-hidden ${then ? "bg-yellow/[0.12]" : "bg-secondary"}`}>
      <div
        className="absolute inset-0 opacity-50"
        style={{ backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 11px, ${hatch} 11px, ${hatch} 12px)` }}
      />
      <span className={`relative font-serif text-3xl lg:text-[3.5rem] italic ${accent} ${then ? "opacity-60" : ""}`}>{initials(name)}</span>
    </div>
  );
};

/**
 * Then & now polaroid card — the "Today" portrait is the hero,
 * the "College years" portrait sits as a smaller rotated polaroid pinned
 * LOWER in the bottom-left corner (never over the face), with washi tape
 * and the ERA-appropriate school hand-written as a caption. Only one
 * school in the caption, and it reflects when the polaroid photo was
 * taken (e.g., Terri = "Harvard Law School", Keith = "Stanford").
 */
const PolaroidPair = ({ person }) => {
  const label = person.school || "Alma mater";
  const tape = person.tape || "bg-yellow";
  return (
    <div className="relative aspect-[4/5] pb-8 pl-2" data-testid="polaroid-pair">
      {/* Today — hero portrait, framed with layered stacked-frame accents.
          When no "today" photo is provided yet, we show the initials
          placeholder here — the archival "then" polaroid overlay below
          still carries the person's real face. */}
      <div className="absolute inset-0">
        <div className="relative h-full w-full overflow-hidden rounded-md border border-border bg-secondary">
          {person.photoNow ? (
            <img src={person.photoNow} alt={`${person.name} today`} className="h-full w-full object-cover" style={focusStyle(person)} />
          ) : (
            <PhotoPlaceholder name={person.name} accent={person.accent} variant="now" />
          )}
          <span
            className={`absolute bottom-2 right-2 font-serif text-[11px] italic leading-none sm:bottom-3 sm:right-3 sm:text-[13px] ${ person.photoNow ? "text-bone [text-shadow:0_1px_10px_rgba(26,23,20,0.65)]" : "text-olive/70" }`}
          >
            today
          </span>
        </div>
      </div>

      {/* Then — SMALL polaroid pinned LOWER-left. Renders whenever the
          person has an archival "then" photo (the placeholder version
          is fine when they don't — it hangs off the corner like a
          footnote and never climbs into the face zone above). */}
      <div
        className="absolute -left-2 -bottom-6 z-20 w-[32%] rotate-[-5deg] bg-bone p-1 pb-5 shadow-[0_18px_36px_-12px_rgba(26,23,20,0.28),0_2px_6px_-2px_rgba(26,23,20,0.15)] transition-transform duration-300 ease-out hover:rotate-[-1deg] hover:-translate-y-1 sm:p-2 sm:pb-8"
        data-testid="polaroid-then"
      >
        <span
          className={`absolute -top-1 left-[36%] h-2 w-7 rotate-[-6deg] ${tape} opacity-70 shadow-sm sm:-top-1.5 sm:h-3 sm:w-11`}
          aria-hidden="true"
        />
        <div className="relative aspect-[3/4] overflow-hidden">
          {person.photoThen ? (
            <img
              src={person.photoThen}
              alt={`${person.name} at ${label}`}
              className="h-full w-full object-cover [filter:sepia(0.35)_saturate(0.75)_contrast(0.95)]"
            />
          ) : (
            <PhotoPlaceholder name={person.name} accent={person.accent} variant="then" />
          )}
        </div>
        <span className="absolute bottom-1 left-1 right-1 truncate text-center font-serif text-[8px] italic leading-tight text-ink sm:bottom-1.5 sm:left-1.5 sm:right-1.5 sm:text-[13px]">
          {label}
        </span>
      </div>
    </div>
  );
};

/** Bio: first paragraph always; the rest behind a "More about {First}" toggle. */
const Bio = ({ person }) => {
  const [open, setOpen] = useState(false);
  const paras = (person.bio || "").split(/\n\s*\n/).filter((p) => p.trim());
  const first = person.name.split(" ")[0];
  const rest = paras.slice(1);
  const slug = person.name.toLowerCase().replace(/[^a-z]+/g, "-");
  if (paras.length === 0) return null;
  return (
    <div className="space-y-3 text-base leading-relaxed text-olive">
      <p>{paras[0]}</p>
      {rest.length > 0 && (
        <>
          <div
            id={`bio-more-${slug}`}
            className={`space-y-3 overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${open ? "max-h-[200rem] opacity-100" : "max-h-0 opacity-0"}`}
            aria-hidden={!open}
          >
            {rest.map((para, i) => <p key={i}>{para}</p>)}
          </div>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls={`bio-more-${slug}`}
            data-testid={`team-bio-toggle-${slug}`}
            className="group inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink underline-offset-4 transition hover:underline"
          >
            {open ? `Less about ${first}` : `More about ${first}`}
            <span className={`inline-block transition-transform duration-300 ${open ? "rotate-180" : ""}`} aria-hidden="true">↓</span>
          </button>
        </>
      )}
    </div>
  );
};

/**
 * Person card. Phones: "index card" — polaroid pair (44% wide) beside the
 * name + role, bio underneath. sm and up: the stacked editorial layout.
 */
const PersonCard = ({ person, i }) => (
  <FadeUp delay={(i % 2) * 0.1}>
    <div id={`member-${memberKey(person.name)}`} data-testid={`team-card-${person.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex scroll-mt-28 flex-col lg:scroll-mt-36">
      <div className="flex items-end gap-5 sm:block">
        <div className="w-[44%] shrink-0 sm:w-auto">
          <PolaroidPair person={person} />
        </div>
        <div className="min-w-0 pb-1 sm:pb-0">
          <h3 className="font-serif text-2xl leading-tight text-ink sm:mt-6 sm:text-3xl">{person.name}</h3>
          <p className="mt-1 font-serif text-lg italic leading-tight text-olive sm:mt-1.5 sm:text-2xl">{person.role}</p>
        </div>
      </div>
      <div className="mt-5 sm:mt-4">
        <Bio person={person} />
      </div>
    </div>
  </FadeUp>
);

/**
 * Founder spotlight — a wider polaroid pair (larger "Today" hero, larger polaroid overlay)
 * paired with the full origin story. Sits at the top of the Team page.
 */
const FounderSpotlight = ({ founder }) => {
  const [before, accent, after] = (() => {
    if (!founder.headingAccent) return [founder.heading, "", ""];
    const [b, a] = founder.heading.split(`{${founder.headingAccent}}`);
    return [b, founder.headingAccent, a];
  })();
  return (
    <section id="founder" className="mx-auto max-w-7xl scroll-mt-24 px-6 pt-4 pb-24 lg:px-8 lg:pb-32">
      <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-5">
          <FadeUp>
            <div className="relative aspect-[4/5] pb-10 pl-3" data-testid="founder-polaroid-pair">
              {/* Today — hero portrait, framed with larger stacked-frame accents.
                  Placeholder tile if Terri doesn't have a "today" shot yet —
                  the archival Harvard Law polaroid below still shows her face. */}
              <div className="absolute inset-0">
                <div className="relative h-full w-full overflow-hidden rounded-md border border-border bg-secondary shadow-[0_20px_50px_-24px_rgba(26,23,20,0.35)]">
                  {founder.photoNow ? (
                    <img
                      src={founder.photoNow}
                      alt={`${founder.name} today`}
                      className="h-full w-full object-cover"
                      style={focusStyle(founder)}
                    />
                  ) : (
                    <PhotoPlaceholder name={founder.name} accent={founder.accent} variant="now" />
                  )}
                  <span
                    className={`absolute bottom-5 right-5 font-serif text-base italic leading-none ${ founder.photoNow ? "text-bone [text-shadow:0_1px_10px_rgba(26,23,20,0.65)]" : "text-olive/70" }`}
                  >
                    today
                  </span>
                </div>
              </div>

              {/* Then — SMALL polaroid pinned LOWER-left. Era-appropriate
                  school caption (Terri = "Harvard Law School"). Always
                  renders; falls back to a placeholder tile when the
                  archival photo hasn't been uploaded yet. */}
              <div
                className="absolute -left-3 -bottom-8 z-20 w-[38%] rotate-[-5deg] bg-bone p-2.5 pb-9 shadow-[0_22px_44px_-14px_rgba(26,23,20,0.32),0_2px_6px_-2px_rgba(26,23,20,0.18)] transition-transform duration-300 ease-out hover:rotate-[-1deg] hover:-translate-y-1"
                data-testid="founder-polaroid-then"
              >
                <span
                  className={`absolute -top-2 left-[36%] h-3.5 w-14 rotate-[-6deg] ${founder.tape || "bg-yellow"} opacity-70 shadow-sm`}
                  aria-hidden="true"
                />
                <div className="relative aspect-[3/4] overflow-hidden">
                  {founder.photoThen ? (
                    <img
                      src={founder.photoThen}
                      alt={`${founder.name} at ${founder.school || "college"}`}
                      className="h-full w-full object-cover [filter:sepia(0.35)_saturate(0.75)_contrast(0.95)]"
                    />
                  ) : (
                    <PhotoPlaceholder name={founder.name} accent={founder.accent} variant="then" />
                  )}
                </div>
                <span className="absolute bottom-2 left-2 right-2 text-center font-serif text-[13px] italic leading-tight text-ink">
                  {founder.school || "Alma mater"}
                </span>
              </div>
            </div>
            <p className="mt-10 font-serif text-3xl italic leading-tight text-olive">{founder.name}</p>
            <span className="mt-1 inline-block -rotate-[1.5deg] origin-left font-caveat text-3xl leading-none text-tangerine">
              {founder.role.toLowerCase()}
            </span>
            {founder.captionQuote && (
              <p
                data-testid="founder-caption-quote"
                className="mt-2 max-w-xs font-serif text-base italic leading-snug text-ink"
              >
                &ldquo;{founder.captionQuote}&rdquo;
              </p>
            )}
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          {/* Audit item L5: the hook first. Terri's ethos opens the column as
              the section headline, the former heading becomes the deck, the
              bio follows with 24px between paragraphs, and her signature
              closes the column instead of a second big quote. */}
          <FadeUp>
            <NoteEyebrow accent="bg-grass">{founder.eyebrow || "our story"}</NoteEyebrow>
            {founder.pullQuote ? (() => {
              const [pBefore, pAfter] = founder.pullQuoteAccent
                ? founder.pullQuote.split(`{${founder.pullQuoteAccent}}`)
                : [founder.pullQuote, ""];
              return (
                <blockquote data-testid="founder-pull-quote" className="mt-5">
                  <p className="font-serif text-3xl lg:text-[3.5rem] italic leading-[1.02] tracking-tight text-ink">
                    &ldquo;{pBefore}
                    {founder.pullQuoteAccent && <EmSwash color="text-grass">{founder.pullQuoteAccent}</EmSwash>}
                    {pAfter}&rdquo;
                  </p>
                </blockquote>
              );
            })() : null}
            <h2 className="mt-8 max-w-2xl font-serif text-3xl leading-snug text-ink" data-testid="founder-deck">
              {before}
              {accent && <EmSwash color={founder.headingAccentColor || "text-cobalt"}>{accent}</EmSwash>}
              {after}
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-10 max-w-2xl space-y-6 text-base leading-relaxed text-olive" data-testid="founder-story">
              {founder.story.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div className="mt-12" data-testid="founder-signature-slot">
              <FounderSignature size="md" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const Group = ({ id, overline, title, subtitle, people }) => people.length === 0 ? null : (
  <section id={id} className="mx-auto max-w-7xl px-6 py-20 lg:px-8 lg:py-24">
    <div className="max-w-3xl">
      <FadeUp>
        <NoteEyebrow accent="bg-cobalt">{overline}</NoteEyebrow>
        <h2 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">{title}</h2>
        <p className="mt-4 text-base leading-relaxed text-olive">{subtitle}</p>
      </FadeUp>
    </div>
    <div className="mt-10 grid grid-cols-1 gap-x-10 gap-y-12 sm:mt-14 sm:grid-cols-2 sm:gap-y-16">
      {people.map((p, i) => <PersonCard key={p.name} person={p} i={i} />)}
    </div>
  </section>
);

export default function Team() {
  const [createOpen, setCreateOpen] = useState(false);
  const teamData = useTeamData();
  const [donateOpen, setDonateOpen] = useState(false);
  const location = useLocation();

  // Scroll to the hash anchor on load / when hash changes (e.g., /team#founder)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
  }, [location.hash]);

  const { founder, board, advisors } = buildRoster(teamData);

  return (
    <main data-testid="team-page" className="bg-sand">
      <SEO
        title="Meet the Team — My Rich Aunt"
        description="The founder, board, and advisors behind My Rich Aunt — the 501(c)(3) making direct, tax-deductible grants to verified college and graduate students."
        path="/team"
        image="/api/og/team.jpg"
        imageAlt="The founder, board and advisors of My Rich Aunt"
      />
      <Navbar onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />

      {/* Page header */}
      <LitGround>
      <header className="mx-auto max-w-7xl px-6 pt-40 pb-8 lg:px-8 lg:pt-48">
        <FadeUp tier="sm">
          <PageAccent className="mb-5" />
        </FadeUp>
        <FadeUp tier="sm">
          <NoteEyebrow accent="bg-tangerine">meet the team</NoteEyebrow>
        </FadeUp>
        <h1 className="mt-6 max-w-4xl font-serif text-[3.5rem] lg:text-[5.5rem] leading-[0.95] tracking-tight text-ink">
          <FadeUp delay={0.05} tier="lg">The people <EmSwash>paying it forward.</EmSwash></FadeUp>
        </h1>
        <FadeUp delay={0.1}>
          <p className="mt-10 max-w-2xl text-base leading-relaxed text-olive">
            My Rich Aunt started with one story, and grew into a whole community of them. Below, meet the founder,
            and the board and advisors who make sure the next generation of students has an easier road.
          </p>
        </FadeUp>
        <FadeUp delay={0.15}>
          <TeamGlance founder={founder} board={board} advisors={advisors} />
        </FadeUp>
      </header>
      </LitGround>

      <FounderSpotlight founder={founder} />

      <div className="border-t border-border" />

      <Group
        id="board"
        overline="governance"
        title="Board of directors"
        subtitle="The people who guide our mission, protect our promise to donors, and make sure My Rich Aunt is built for longevity."
        people={board}
      />

      <div className="border-t border-border" />

      <Group
        id="advisors"
        overline="guidance"
        title="Advisory board"
        subtitle="The mentors who challenge our thinking and keep us focused on the students."
        people={advisors}
      />


      <Footer onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />

      <CreateCampaign open={createOpen} onOpenChange={setCreateOpen} />
      <DonateComingSoonDialog open={donateOpen} onOpenChange={setDonateOpen} />
    </main>
  );
}
